import { supabase } from "../supabaseClient.js";

// Todas estas consultas dependen de RLS: lo que devuelve Supabase ya viene
// filtrado por rol. La UI no hace de guardia de seguridad, sólo de comodidad.
//
// Ojo con la separación admin/empleado:
//   events / packages / products  → los dos roles
//   event_finance / package_rates / movements / seasons / enrollments /
//   audit_log                     → sólo admin (sin política para empleado)
// Por eso hay dos funciones para traer eventos: fetchEvents() usa la vista
// con importes (admin) y fetchMyEvents() la tabla pelada (empleado).

const EVENT_FIELDS =
  "id, description, product_id, package_id, series_id, date, start_time, end_time, sale_date, created_by, children_count, teachers_count, souvenirs_count, details, notes, child_name, contact, guardian_name, guardian_contact, child_age, allergies, email, assigned_coordinator";

const FINANCE_FIELDS =
  "cost_lines, price_lines, reviewed_by, reviewed_at, source, deposit, settled_at, balance_pending";

// ---------- Productos ----------

export async function fetchProducts() {
  const { data, error } = await supabase
    .from("products")
    .select("id, name, color, slug, kind, form_schema, active")
    .order("name");
  if (error) throw error;
  return data;
}

export async function createProduct(product) {
  const { error } = await supabase.from("products").insert(product);
  if (error) throw error;
}

export async function updateProduct(id, changes) {
  const { error } = await supabase.from("products").update(changes).eq("id", id);
  if (error) throw error;
}

export async function deleteProduct(id) {
  const { error } = await supabase.from("products").delete().eq("id", id);
  if (error) throw error;
}

// ---------- Paquetes ----------

/** Nombres de paquete: los ve cualquier rol (el empleado tiene que elegir uno). */
export async function fetchPackages() {
  const { data, error } = await supabase
    .from("packages")
    .select("id, product_id, name, description, sort_order, active")
    .eq("active", true)
    .order("sort_order");
  if (error) throw error;
  return data;
}

/** Tarifas de cada paquete: sólo admin. Para un empleado vuelve vacío. */
export async function fetchPackageRates() {
  const { data, error } = await supabase.from("package_rates").select("*");
  if (error) throw error;
  return data;
}

export async function savePackage(pkg, rates, id = null) {
  let packageId = id;

  if (id) {
    const { error } = await supabase.from("packages").update(pkg).eq("id", id);
    if (error) throw error;
  } else {
    const { data, error } = await supabase.from("packages").insert(pkg).select("id").single();
    if (error) throw error;
    packageId = data.id;
  }

  const { error: ratesError } = await supabase
    .from("package_rates")
    .upsert({ ...rates, package_id: packageId });
  if (ratesError) throw ratesError;

  return packageId;
}

export async function deletePackage(id) {
  const { error } = await supabase.from("packages").delete().eq("id", id);
  if (error) throw error;
}

// ---------- Eventos ----------

/** Admin: eventos con costo y ganancia calculados en la base. */
export async function fetchEvents() {
  const { data, error } = await supabase
    .from("event_financials")
    .select("*")
    .order("date", { ascending: false }).order("start_time", { ascending: true, nullsFirst: false });
  if (error) throw error;
  return data;
}

/**
 * Admin: todos los eventos, tengan o no datos financieros cargados. Los que
 * cargó un empleado y todavía no se revisaron no aparecen en event_financials,
 * porque esa vista hace join con event_finance.
 */
export async function fetchAllEventsWithFinance() {
  const [{ data: events, error }, { data: finance, error: financeError }] = await Promise.all([
    supabase.from("events").select(EVENT_FIELDS).order("date", { ascending: false }).order("start_time", { ascending: true, nullsFirst: false }),
    supabase.from("event_finance").select(`event_id, ${FINANCE_FIELDS}`),
  ]);
  if (error) throw error;
  if (financeError) throw financeError;

  const byEvent = new Map((finance ?? []).map((f) => [f.event_id, f]));
  return events.map((e) => ({ ...e, finance: byEvent.get(e.id) ?? null }));
}

/** Empleado: sus propios eventos, sin ninguna columna de plata. */
export async function fetchMyEvents() {
  const { data, error } = await supabase
    .from("events")
    .select(EVENT_FIELDS)
    .order("date", { ascending: false });
  if (error) throw error;
  return data;
}

export async function saveEvent(event, id = null) {
  if (id) {
    const { error } = await supabase.from("events").update(event).eq("id", id);
    if (error) throw error;
    return id;
  }
  const { data, error } = await supabase.from("events").insert(event).select("id").single();
  if (error) throw error;
  return data.id;
}

/** Sólo admin: crea o actualiza la parte financiera de un evento. */
/**
 * Guardar desde la app es siempre un ajuste de la familia: queda marcado como
 * 'ajustado' para que el trigger del paquete no lo vuelva a pisar.
 */
export async function saveEventFinance(eventId, finance) {
  const { error } = await supabase
    .from("event_finance")
    .upsert({ ...finance, event_id: eventId, source: "ajustado" });
  if (error) throw error;
}

export async function markEventReviewed(eventId) {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  const { error } = await supabase
    .from("event_finance")
    .update({ reviewed_by: user?.id, reviewed_at: new Date().toISOString() })
    .eq("event_id", eventId);
  if (error) throw error;
}

export async function deleteEvent(id) {
  const { error } = await supabase.from("events").delete().eq("id", id);
  if (error) throw error;
}

/** Crea una serie y devuelve las fechas generadas, para eventos repetidos. */
export async function createEventSeries(series, dates, eventTemplate) {
  const { data, error } = await supabase.from("event_series").insert(series).select("id").single();
  if (error) throw error;

  const rows = dates.map((date) => ({ ...eventTemplate, date, series_id: data.id }));
  const { data: created, error: eventsError } = await supabase.from("events").insert(rows).select("id");
  if (eventsError) throw eventsError;

  return { seriesId: data.id, eventIds: created.map((e) => e.id) };
}

// ---------- Movimientos ----------

export async function fetchMovements() {
  const { data, error } = await supabase
    .from("movements")
    .select("id, type, amount, date, category, description, event_id, auto_generated, created_by")
    .order("date", { ascending: false });
  if (error) throw error;
  return data;
}

export async function createMovement(movement) {
  const { error } = await supabase.from("movements").insert(movement);
  if (error) throw error;
}

export async function deleteMovement(id) {
  const { error } = await supabase.from("movements").delete().eq("id", id);
  if (error) throw error;
}

// ---------- Colonia: temporadas e inscripciones ----------

export async function fetchSeasons() {
  const { data, error } = await supabase
    .from("seasons")
    .select("*")
    .order("season_start", { ascending: false });
  if (error) throw error;
  return data;
}

export async function saveSeason(season, id = null) {
  if (id) {
    const { error } = await supabase.from("seasons").update(season).eq("id", id);
    if (error) throw error;
    return id;
  }
  const { data, error } = await supabase.from("seasons").insert(season).select("id").single();
  if (error) throw error;
  return data.id;
}

export async function deleteSeason(id) {
  const { error } = await supabase.from("seasons").delete().eq("id", id);
  if (error) throw error;
}

export async function fetchEnrollments(seasonId) {
  let query = supabase.from("enrollments").select("*").order("child_name");
  if (seasonId) query = query.eq("season_id", seasonId);
  const { data, error } = await query;
  if (error) throw error;
  return data;
}

export async function saveEnrollment(enrollment, id = null) {
  if (id) {
    const { error } = await supabase.from("enrollments").update(enrollment).eq("id", id);
    if (error) throw error;
    return id;
  }
  const { data, error } = await supabase.from("enrollments").insert(enrollment).select("id").single();
  if (error) throw error;
  return data.id;
}

export async function deleteEnrollment(id) {
  const { error } = await supabase.from("enrollments").delete().eq("id", id);
  if (error) throw error;
}

// ---------- Auditoría ----------

export async function fetchAuditLog({ limit = 100 } = {}) {
  const { data, error } = await supabase
    .from("audit_log")
    .select("id, table_name, row_id, action, actor_name, at, before, after")
    .order("at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data;
}

// ---------- Personas ----------

export async function fetchProfiles() {
  const { data, error } = await supabase
    .from("profiles")
    .select("id, name, role, active, deactivated_at, created_at")
    .order("name");
  if (error) throw error;
  return data;
}

export async function updateProfile(id, changes) {
  const { error } = await supabase.from("profiles").update(changes).eq("id", id);
  if (error) throw error;
}

/**
 * Da de baja a alguien sin borrarle el historial: is_active() deja de dar
 * verdadero y con eso no pasa ninguna política RLS.
 */
export async function setProfileActive(id, active) {
  const { error } = await supabase
    .from("profiles")
    .update({ active, deactivated_at: active ? null : new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

// ---------- Invitaciones ----------

/**
 * Lista de emails habilitados a registrarse. Sin fila acá, el alta se rechaza
 * por trigger en la base: el registro es sólo por invitación.
 */
export async function fetchInvitations() {
  const { data, error } = await supabase
    .from("allowed_signups")
    .select("email, name, role, created_at, used_at")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data;
}

export async function createInvitation({ email, name, role }) {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  const { error } = await supabase.from("allowed_signups").insert({
    email: email.trim().toLowerCase(),
    name: name?.trim() || null,
    role,
    invited_by: user?.id,
  });
  if (error) throw error;
}

export async function deleteInvitation(email) {
  const { error } = await supabase.from("allowed_signups").delete().eq("email", email);
  if (error) throw error;
}

/**
 * Alta directa con contraseña, vía Cloudflare Worker. Opcional: crear la
 * invitación y que la persona se registre sola alcanza, y no requiere
 * desplegar el Worker ni manejar la service role key.
 */
export async function inviteUser({ email, name, role }) {
  const workerUrl = import.meta.env.VITE_INVITE_WORKER_URL;
  if (!workerUrl) throw new Error("Falta configurar VITE_INVITE_WORKER_URL.");

  const {
    data: { session },
  } = await supabase.auth.getSession();

  const response = await fetch(`${workerUrl.replace(/\/$/, "")}/invite`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session?.access_token}`,
    },
    body: JSON.stringify({ email, name, role }),
  });

  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || "No se pudo invitar a la persona.");
  return body;
}
